import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useSession } from "../../auth/session"
import Api from "../../api/api"
import { AlModel, License, Product, providerLabels, Sku, Trial } from "../../api/models"
import Spinner from "../widgets/Spinner"
import OrgChip from "../widgets/OrgChip"
import UserChip from "../widgets/UserChip"
import CardImage from "../widgets/CardImage"
import SkuName from "../widgets/SkuName"
import CopyableId from "../widgets/CopyableId"
import { alIcon, AlLogo, creditsIcon, trialIcon } from "../widgets/pillIcons"
import { formatExpiryDate, isLocked } from "../widgets/licenseCells"
import { formatDate, OriginCell, SkuCell } from "../UserLicensesTable"
import EditProductDialog, { DeleteProductDialog } from "../EditProductDialog"

const ROWS_PAGE_SIZE = 10

const rowActionStyle = { background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" } as const

// A dollar amount without trailing .00 noise ($99, $79.50)
function formatValue(valueCents: number): string {
  return "$" + (valueCents % 100 === 0 ? String(valueCents / 100) : (valueCents / 100).toFixed(2))
}

const offeringLabels = { one_time: "One-time", subscription: "Subscription", free: "Free", reward: "Reward" } as const

// The claim-tile pattern from SkuPage's Content card, pointed the other way:
// a sku the product belongs to, clicking through to /skus/:id. The terms
// line carries the latest version and how the sku sells.
function SkuTile({ sku, unreleased }: { sku: Sku, unreleased?: boolean }) {
  const navigate = useNavigate()
  const terms: string[] = []
  // the product is only in an unreleased version of this sku — not on the
  // store yet
  if (unreleased) terms.push("Unreleased")
  if (sku.latestVersion != null) terms.push(`v${sku.latestVersion}`)
  if (sku.offerings.length === 0) {
    terms.push("Not shipped")
  } else {
    const kinds = new Set(sku.offerings.map(o => o.paymentType))
    const order = ["one_time", "subscription", "free", "reward"] as const
    order.filter(k => kinds.has(k)).forEach(k => terms.push(offeringLabels[k]))
  }
  return (
    <div className="claim-tile claim-tile-clickable" onClick={() => navigate(`/skus/${sku.id}`)}>
      {/* a 16:9 placeholder keeps the tile shape for image-less skus */}
      <CardImage
        imageUrl={sku.imageUrl}
        availableWidths={sku.availableWidths}
        alt={sku.name}
        placeholderAspect="16 / 9"
      />
      <div className="claim-tile-name"><SkuName sku={sku} variant="section" sizes="160px" /></div>
      {terms.length > 0 && <div className="claim-tile-terms">{terms.join(" · ")}</div>}
    </div>
  )
}

// The product's licenses — every deposited license row across all users,
// newest first with cursor load-more (the CodeBatchesCard pattern). The
// remount key is the productId, so state is safely local.
function ProductLicensesCard({ productId, accessToken }: { productId: string, accessToken: string }) {
  const [licenses, setLicenses] = useState<(License & { skuId?: string })[] | undefined>(undefined)
  const [total, setTotal] = useState<number | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const requestSeq = useRef(0)

  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.adminGetProductLicenses({ productId, link: cursor ?? undefined, count: ROWS_PAGE_SIZE, accessToken })
      if (seq !== requestSeq.current) return
      setLicenses(prev => cursor === null ? page.licenses : [...(prev ?? []), ...page.licenses])
      if (cursor === null) setTotal(page.total)
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [productId, accessToken])

  useEffect(() => {
    requestSeq.current++
    setLicenses(undefined)
    setLink(null)
    loadPage(null)
  }, [loadPage])

  return (
    <div className="tab-card" style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <h2 className="section-title tab-card-tab">Licenses</h2>
        {total !== null && (
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>{total} total</span>
        )}
      </div>
      <div className="tab-card-body">
        {error && licenses === undefined ? (
          <p className="empty-state">Error loading licenses</p>
        ) : licenses === undefined ? (
          <p className="empty-state"><Spinner size={16} /> Loading licenses…</p>
        ) : licenses.length === 0 ? (
          <p className="empty-state">None — licenses land here as users buy, redeem or get granted a SKU claiming this product.</p>
        ) : (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>SKU</th>
                    <th>Origin</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Activations</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license) => (
                    <tr key={license.id} className={license.expired ? "row-muted" : undefined}>
                      <td>
                        <UserChip userId={license.userId} />
                        {isLocked(license) && <span className="badge-locked">Locked</span>}
                      </td>
                      <SkuCell skuId={license.skuId} />
                      <OriginCell license={license} />
                      <td>{formatDate(license.createdAt)}</td>
                      <td>
                        {formatExpiryDate(license)}
                        {license.expired && (
                          <span className="status-badge status-expired" style={{ marginLeft: 6 }}>expired</span>
                        )}
                      </td>
                      <td>{license.activationIds.active.length} / {license.maxActivations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {link !== null && (
              loading ? <Spinner size={16} /> : (
                <button className="card-link" style={{ ...rowActionStyle, marginTop: 12 }} onClick={() => loadPage(link)}>
                  Load more
                </button>
              )
            )}
            {error && <p className="empty-state">Error loading more licenses</p>}
          </>
        )}
      </div>
    </div>
  )
}

// Every trial ever started for the product (the once-per-user-product
// record — expired rows stay, they're why a user can't trial twice).
function ProductTrialsCard({ productId, accessToken }: { productId: string, accessToken: string }) {
  const [trials, setTrials] = useState<Trial[] | undefined>(undefined)
  const [total, setTotal] = useState<number | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const requestSeq = useRef(0)

  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.adminGetProductTrials({ productId, link: cursor ?? undefined, count: ROWS_PAGE_SIZE, accessToken })
      if (seq !== requestSeq.current) return
      setTrials(prev => cursor === null ? page.trials : [...(prev ?? []), ...page.trials])
      if (cursor === null) setTotal(page.total)
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [productId, accessToken])

  useEffect(() => {
    requestSeq.current++
    setTrials(undefined)
    setLink(null)
    loadPage(null)
  }, [loadPage])

  return (
    <div className="tab-card" style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <h2 className="section-title tab-card-tab">Trials</h2>
        {total !== null && (
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>{total} total</span>
        )}
      </div>
      <div className="tab-card-body">
        {error && trials === undefined ? (
          <p className="empty-state">Error loading trials</p>
        ) : trials === undefined ? (
          <p className="empty-state"><Spinner size={16} /> Loading trials…</p>
        ) : trials.length === 0 ? (
          <p className="empty-state">None yet</p>
        ) : (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Started</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {trials.map((trial) => (
                    <tr key={trial.id} className={trial.expired ? "row-muted" : undefined}>
                      <td><UserChip userId={trial.userId} /></td>
                      <td>{formatDate(trial.startedAt)}</td>
                      <td>
                        {formatDate(trial.expiresAt)}
                        {trial.expired && (
                          <span className="status-badge status-expired" style={{ marginLeft: 6 }}>expired</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {link !== null && (
              loading ? <Spinner size={16} /> : (
                <button className="card-link" style={{ ...rowActionStyle, marginTop: 12 }} onClick={() => loadPage(link)}>
                  Load more
                </button>
              )
            )}
            {error && <p className="empty-state">Error loading more trials</p>}
          </>
        )}
      </div>
    </div>
  )
}

// One model of the product's A.L. wiring: the provider's model id with the
// provider name underneath — the internal models.id stays hidden ("—" when
// the config points at a missing model row).
function AlModelItem({ label, model }: { label: string, model?: AlModel | null }) {
  return (
    <div className="detail-item">
      <div className="detail-label">{label}</div>
      <div className="detail-value" style={{ overflowWrap: "anywhere" }} title={model?.name}>
        {model?.providerModelId ?? "—"}
      </div>
      {model != null && (
        <div style={{ color: "var(--nfa-light-grey)", fontSize: "9pt", marginTop: 2 }}>
          {providerLabels[model.providerId] ?? `Provider ${model.providerId}`}
        </div>
      )}
    </div>
  )
}

// Admin/dev product detail page in the SkuPage idiom: title row, hero/meta
// block, then vertical cards — SKUs (tiles), Licenses, Trials, and a
// read-only AI assistant card when the product supports it. Developers only
// reach their own products (server-side 403).
export default function ProductPage() {
  const { id } = useParams<{ id: string }>()
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const navigate = useNavigate()

  const [product, setProduct] = useState<Product | undefined>(undefined)
  const [skus, setSkus] = useState<Sku[]>([])
  const [upcomingSkus, setUpcomingSkus] = useState<Sku[]>([])
  const [alModels, setAlModels] = useState<{ orchestrator: AlModel | null, worker: AlModel | null, description: AlModel | null } | undefined>(undefined)
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(false)
  // stale responses (product changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  useEffect(() => {
    const seq = ++requestSeq.current
    setProduct(undefined)
    setError(false)
    Api.adminGetProduct({ productId: id!, accessToken })
      .then(({ product, skus, upcomingSkus, alModels }) => {
        if (seq !== requestSeq.current) return
        setProduct(product)
        setSkus(skus)
        setUpcomingSkus(upcomingSkus)
        setAlModels(alModels)
      })
      .catch(() => {
        if (seq === requestSeq.current) setError(true)
      })
  }, [id, accessToken])

  if (error) {
    return (
      <div className="page">
        <p className="empty-state">Error loading product</p>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="page">
        <p className="empty-state"><Spinner size={16} /> Loading product…</p>
      </div>
    )
  }

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <h1 className="page-title" style={{ marginBottom: 4 }}>{product.name}</h1>
          <CopyableId id={product.id} />
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <button
            className="card-link"
            style={rowActionStyle}
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
          <button
            className="card-link-danger"
            style={rowActionStyle}
            onClick={() => setDeleting(true)}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="license-group license-group-with-img license-group-plain">
        <div className="license-group-img-container">
          <CardImage imageUrl={product.imageUrl} availableWidths={product.availableWidths} alt={product.name} />
        </div>
        <div className="product-group-header">
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
            {product.valueCents > 0
              ? <>Value: <span style={{ color: "var(--nfa-off-white)" }}>{formatValue(product.valueCents)}</span></>
              : "No standalone value"}
          </span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            {product.al != null && (
              <span className="badge badge-logo" title="Supports the A.L. assistant">{alIcon}</span>
            )}
            {product.needsCredits && (
              <span className="badge">{creditsIcon}Pay-as-you-go</span>
            )}
            {product.trialDays !== undefined && product.trialDays > 0 ? (
              <span className="badge" title={`${product.trialMaxActivations ?? "—"} trial activations`}>
                {trialIcon}{product.trialDays}-day trial
              </span>
            ) : (
              <span className="badge">No trial</span>
            )}
            <OrgChip orgId={product.ownerId} />
          </div>
        </div>
        {product.url && (
          <p style={{ margin: 0, gridArea: "list", padding: "0 20px 20px" }}>
            <a href={product.url} target="_blank" rel="noreferrer" className="card-link" style={{ fontSize: "10pt" }}>
              Product page ↗
            </a>
          </p>
        )}
      </div>

      <div className="tab-card" style={{ marginTop: 32 }}>
        {/* read-only: a product joins a sku when a version's claim set pulls
            it in — that's defined in the SKU page's Add Version dialog */}
        <h2 className="section-title tab-card-tab">SKUs</h2>
        <div className="tab-card-body">
          {skus.length === 0 && upcomingSkus.length === 0 ? (
            <p className="empty-state">
              Not in any SKU's current or upcoming version — a version's
              claims pull it in, and only then can it be sold or granted.
            </p>
          ) : (
            <div className="claim-tile-grid">
              {skus.map((sku) => <SkuTile key={sku.id} sku={sku} />)}
              {upcomingSkus.map((sku) => <SkuTile key={sku.id} sku={sku} unreleased />)}
            </div>
          )}
        </div>
      </div>

      <ProductLicensesCard productId={product.id} accessToken={accessToken} />
      <ProductTrialsCard productId={product.id} accessToken={accessToken} />

      {product.al != null && (
        <div className="tab-card" style={{ marginTop: 32 }}>
          {/* read-only: AL wiring is not editable through the product
              endpoints (manual SQL for now) */}
          {/* inline-flex, not flex — the tab look needs the h2 to
              shrink-wrap its content like the other cards' inline-block */}
          <h2 className="section-title tab-card-tab" aria-label="A.L." style={{ display: "inline-flex", alignItems: "center" }}>
            <AlLogo height={16} />
          </h2>
          <div className="tab-card-body">
            <div className="detail-grid">
              <AlModelItem label="Orchestrator Model" model={alModels?.orchestrator} />
              <AlModelItem label="Worker Model" model={alModels?.worker} />
              <AlModelItem label="Description Model" model={alModels?.description} />
            </div>
          </div>
        </div>
      )}

      {editing && (
        <EditProductDialog
          product={product}
          accessToken={accessToken}
          onClose={() => setEditing(false)}
          onSaved={(updated) => setProduct(updated)}
        />
      )}
      {deleting && (
        <DeleteProductDialog
          product={product}
          accessToken={accessToken}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate("/products")}
        />
      )}
    </div>
  )
}
